import { supabase } from './supabase';
import { UserProfile } from './types';

export class UserProfileService {
  private tableName = 'user_profiles';

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      // Set the user context for RLS
      await supabase.rpc('set_config', {
        parameter: 'app.current_user_id',
        value: userId
      });

      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - user profile doesn't exist yet
          return null;
        }
        console.error('Error fetching user profile:', error);
        throw error;
      }

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        degreePursued: data.degree_pursued,
        totalCUsRequired: data.total_cus_required,
        startTerm: data.start_term,
        startDate: data.start_date,
        graduationGoal: data.graduation_goal,
        studyHoursPerWeek: data.study_hours_per_week,
        preferredStudyDays: data.preferred_study_days,
        accelerationMode: data.acceleration_mode,
        role: data.role,
        setupCompleted: data.setup_completed,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      throw error;
    }
  }

  async createUserProfile(profile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<UserProfile> {
    try {
      // Set the user context for RLS
      await supabase.rpc('set_config', {
        parameter: 'app.current_user_id',
        value: profile.userId
      });

      const { data, error } = await supabase
        .from(this.tableName)
        .insert({
          user_id: profile.userId,
          name: profile.name,
          degree_pursued: profile.degreePursued,
          total_cus_required: profile.totalCUsRequired,
          start_term: profile.startTerm,
          start_date: profile.startDate,
          graduation_goal: profile.graduationGoal,
          study_hours_per_week: profile.studyHoursPerWeek,
          preferred_study_days: profile.preferredStudyDays,
          acceleration_mode: profile.accelerationMode,
          role: profile.role,
          setup_completed: profile.setupCompleted,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        throw error;
      }

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        degreePursued: data.degree_pursued,
        totalCUsRequired: data.total_cus_required,
        startTerm: data.start_term,
        startDate: data.start_date,
        graduationGoal: data.graduation_goal,
        studyHoursPerWeek: data.study_hours_per_week,
        preferredStudyDays: data.preferred_study_days,
        accelerationMode: data.acceleration_mode,
        role: data.role,
        setupCompleted: data.setup_completed,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };
    } catch (error) {
      console.error('Error in createUserProfile:', error);
      throw error;
    }
  }

  async updateUserProfile(
    userId: string,
    updates: Partial<Omit<UserProfile, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>
  ): Promise<UserProfile> {
    try {
      // Set the user context for RLS
      await supabase.rpc('set_config', {
        parameter: 'app.current_user_id',
        value: userId
      });

      const updateData: any = {};
      
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.degreePursued !== undefined) updateData.degree_pursued = updates.degreePursued;
      if (updates.totalCUsRequired !== undefined) updateData.total_cus_required = updates.totalCUsRequired;
      if (updates.startTerm !== undefined) updateData.start_term = updates.startTerm;
      if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
      if (updates.graduationGoal !== undefined) updateData.graduation_goal = updates.graduationGoal;
      if (updates.studyHoursPerWeek !== undefined) updateData.study_hours_per_week = updates.studyHoursPerWeek;
      if (updates.preferredStudyDays !== undefined) updateData.preferred_study_days = updates.preferredStudyDays;
      if (updates.accelerationMode !== undefined) updateData.acceleration_mode = updates.accelerationMode;
      if (updates.role !== undefined) updateData.role = updates.role;
      if (updates.setupCompleted !== undefined) updateData.setup_completed = updates.setupCompleted;

      const { data, error } = await supabase
        .from(this.tableName)
        .update(updateData)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user profile:', error);
        throw error;
      }

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        degreePursued: data.degree_pursued,
        totalCUsRequired: data.total_cus_required,
        startTerm: data.start_term,
        startDate: data.start_date,
        graduationGoal: data.graduation_goal,
        studyHoursPerWeek: data.study_hours_per_week,
        preferredStudyDays: data.preferred_study_days,
        accelerationMode: data.acceleration_mode,
        role: data.role,
        setupCompleted: data.setup_completed,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };
    } catch (error) {
      console.error('Error in updateUserProfile:', error);
      throw error;
    }
  }

  async deleteUserProfile(userId: string): Promise<boolean> {
    try {
      // Set the user context for RLS
      await supabase.rpc('set_config', {
        parameter: 'app.current_user_id',
        value: userId
      });

      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error deleting user profile:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteUserProfile:', error);
      return false;
    }
  }

  async upsertUserProfile(profile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<UserProfile> {
    try {
      const existingProfile = await this.getUserProfile(profile.userId);
      
      if (existingProfile) {
        return await this.updateUserProfile(profile.userId, profile);
      } else {
        return await this.createUserProfile(profile);
      }
    } catch (error) {
      console.error('Error in upsertUserProfile:', error);
      throw error;
    }
  }
}

export const userProfileService = new UserProfileService(); 